import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { useButton } from 'react-aria';
import {
  type CollectionPairing,
  type CollectionProduct,
  type CartPairing,
  type CartProduct,
  type PairingV1 as Pairing,
  type ProductV1 as Product,
} from '@hungryroot/rest';
import { Minus, Plus, Heading, Loading } from '@hungryroot/ui-kit';
import {
  getCartItemKind,
  useIsCartActionPending,
  type CartItemKind,
} from '../PendingCartActions/PendingCartActions';
import styles from './AddRemoveButton.module.css';

interface Props {
  ariaLabel?: string;
  addProduct?: (
    recipe:
      | Pairing
      | Product
      | CartPairing
      | CartProduct
      | CollectionProduct
      | CollectionPairing
  ) => void;
  removeProduct?: (
    recipe:
      | Pairing
      | Product
      | CartPairing
      | CartProduct
      | CollectionProduct
      | CollectionPairing
  ) => void;
  productData:
    | Pairing
    | Product
    | CartPairing
    | CartProduct
    | CollectionProduct
    | CollectionPairing;
  isNotAddable: () => boolean;
  cartCount: number;
  onAddToCart?: () => void;
  testid?: string;
  displayCountButtonClassName?: string;
  addOnlyButtonClassName?: string;
  addRemoveButtonWrapperClassName?: string;
  /**
   * Whether `productData` is a recipe or a product. Pass it when the caller
   * knows: some callers hand over a trimmed object (e.g. `{ id, name }`) that
   * the shape check can't tell apart.
   */
  cartItemKind?: CartItemKind;
}

export function AddRemoveButton(props: Props) {
  const {
    addProduct,
    removeProduct,
    productData,
    isNotAddable,
    cartCount,
    onAddToCart,
    testid,
    displayCountButtonClassName,
    addOnlyButtonClassName,
    addRemoveButtonWrapperClassName,
    cartItemKind,
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);
  const isPending = useIsCartActionPending(
    cartItemKind ?? getCartItemKind(productData),
    productData.id
  );
  const shouldFocusAddRef = useRef(false);
  const { name: productName } = productData;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { buttonProps: countButtonProps } = useButton(
    {
      onPress: () => {
        setIsExpanded(true);
        shouldFocusAddRef.current = true;
      },
      'aria-label': `${cartCount} in cart,  click to show add and remove buttons`,
    },
    buttonRef
  );

  const disableButton = isNotAddable();

  const { buttonProps: addButtonProps } = useButton(
    {
      onPress: () => {
        if (addProduct) {
          addProduct(productData);
        }
        if (onAddToCart) onAddToCart();
        setIsExpanded(true);
      },
      isDisabled: disableButton,
      'aria-label': `Add ${productName} to cart`,
    },
    buttonRef
  );

  useEffect(() => {
    if (disableButton) {
      document.getElementById(`${productData.id}-remove-button`)?.focus();
    }
  }, [disableButton, productData.id]);

  // Callback ref to focus the add button when it's rendered and should be focused
  const addButtonCallbackRef = (node: HTMLButtonElement | null) => {
    if (node && shouldFocusAddRef.current) {
      node.focus();
      shouldFocusAddRef.current = false;
    }
  };

  const { buttonProps: removeButtonProps } = useButton(
    {
      onPress: () => {
        if (removeProduct) {
          removeProduct(productData);
        }
      },
      'aria-label': `Remove ${productName} from cart`,
    },
    buttonRef
  );

  const renderAddRemoveButton = () => {
    if (isPending) {
      // The add or remove the customer asked for is waiting on the cart (e.g.
      // an unskip that is reshuffling the order). Show it's on its way rather
      // than a + that looks like the tap did nothing.
      return (
        <button
          type="button"
          disabled
          aria-busy="true"
          aria-label={`Updating ${productName} in your cart`}
          className={clsx(
            styles['add-only-button-product-card'],
            addOnlyButtonClassName
          )}
          data-testid={`${testid}-pending`}
        >
          <Loading
            variant="inline-button"
            sizeVariant="small"
            className={styles['pending-dots']}
          />
        </button>
      );
    }

    if (!isExpanded && cartCount > 0) {
      return (
        <button
          {...countButtonProps}
          ref={buttonRef}
          className={clsx(
            styles['display-count-button-product-card'],
            displayCountButtonClassName
          )}
          data-testid={`${testid}-inactive-cart-count-button`}
        >
          <Heading testid={`${testid}-inactive-cart-count`} variant="H6">
            {cartCount}
          </Heading>
        </button>
      );
    }

    return (
      <div
        ref={wrapperRef}
        className={
          isExpanded && cartCount > 0
            ? clsx(
                styles['add-remove-button-wrapper'],
                addRemoveButtonWrapperClassName
              )
            : ''
        }
        onBlur={(e) => {
          if (!wrapperRef.current?.contains(e.relatedTarget)) {
            setIsExpanded(false);
          }
        }}
        data-testid={testid}
      >
        {isExpanded && cartCount > 0 ? (
          <>
            <button
              {...removeButtonProps}
              id={`${productData.id}-remove-button`}
              data-testid={`${testid}-remove-button`}
            >
              <Minus />
            </button>
            <Heading testid={`${testid}-active-cart-count`} variant="H6">
              {cartCount}
            </Heading>
          </>
        ) : null}
        <button
          {...addButtonProps}
          ref={addButtonCallbackRef}
          data-testid={`${testid}-add-button`}
          className={
            cartCount === 0
              ? clsx(
                  styles['add-only-button-product-card'],
                  addOnlyButtonClassName
                )
              : ''
          }
        >
          <Plus />
        </button>
      </div>
    );
  };

  return renderAddRemoveButton();
}
